import { Tag } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import { moneyInterface } from '@/compiler/types';
import baseAxios from '@/helpers/forms/axios';
import { TagResponse, toTag } from '../tags';
import { merchantUserMembershipInvitationInterface, userInterface } from '../users';

interface clientBalanceResponseInterface {
    openCharges: moneyInterface | null
    unpaidCharges: moneyInterface | null
}

// Snake_case response type for loadClientBalance (raw response from backend)
interface ClientBalanceResponseRaw {
    open_charges: moneyInterface | null
    unpaid_charges: moneyInterface | null
}

function toClientBalance(r: ClientBalanceResponseRaw): clientBalanceResponseInterface {
    return {
        openCharges: r.open_charges,
        unpaidCharges: r.unpaid_charges,
    };
}

interface FixCountResponse {
    unresolvedUnsellableItems: number
    negativeQuantityItems: number
    multipleFnskuItems: number
    multipleFnskuListings: number
    ordersWithErrors: number
}

// Snake_case response type for getFixCount (raw response from backend)
interface FixCountResponseRaw {
    unresolved_unsellable_items: number
    negative_quantity_items: number
    multiple_fnsku_items: number
    multiple_fnsku_listings: number
    orders_with_errors: number
}

function toFixCount(r: FixCountResponseRaw): FixCountResponse {
    return {
        unresolvedUnsellableItems: Number(r.unresolved_unsellable_items),
        negativeQuantityItems: Number(r.negative_quantity_items),
        multipleFnskuItems: Number(r.multiple_fnsku_items),
        multipleFnskuListings: Number(r.multiple_fnsku_listings),
        ordersWithErrors: Number(r.orders_with_errors),
    };
}

export interface FixStatusResponse {
    has_warnings: boolean
    has_errors: boolean
}

export enum MerchantUserRole {
    Owner = 'owner',
    Assistant = 'client-assistant',
}

export interface merchantInterface {
    id: number
    uuid: string
    name: string
    notes: string
    primaryEmail: string
    photoUrl: string
    billingCycle: 'monthly' | 'manual'
    billingCycleDay: number,
    perItemAdjustment: number
    enabled: boolean
    isOrdersEnabled: boolean
    isAmazonShipmentsEnabled: boolean
}

export interface createMerchantFormInterface {
    name: string
    primaryEmail: string
    inviteEmail: string | null
    inviteRole: MerchantUserRole | null
}

export interface registerMerchantFormInterface {
    name: string
    primaryEmail: string
}

export interface updateMerchantFormInterface {
    phoneNumber: string
    email: string
    notes: string
    name: string
    billingCycle: string
    billingCycleDay: number
    perItemAdjustment: number
    enabled: boolean
    isOrdersEnabled: boolean
    isAmazonShipmentsEnabled: boolean
}

interface UpdateMerchantFormRaw {
    phone_number: string
    email: string
    notes: string
    name: string
    billing_cycle: string
    billing_cycle_day: number
    per_item_adjustment: number
    enabled: boolean
    is_orders_enabled: boolean
    is_amazon_shipments_enabled: boolean
}

function fromUpdateMerchantForm(data: updateMerchantFormInterface): UpdateMerchantFormRaw {
    return {
        phone_number: data.phoneNumber,
        email: data.email,
        notes: data.notes,
        name: data.name,
        billing_cycle: data.billingCycle,
        billing_cycle_day: data.billingCycleDay,
        per_item_adjustment: data.perItemAdjustment,
        enabled: data.enabled,
        is_orders_enabled: data.isOrdersEnabled,
        is_amazon_shipments_enabled: data.isAmazonShipmentsEnabled,
    };
}

interface MerchantsApiReturn {
    getFixCount: () => Promise<FixCountResponse>
    getFixStatus: () => Promise<FixStatusResponse>
    loadClientBalance: (clientId: number) => Promise<clientBalanceResponseInterface>
    fetchMerchantPendingInvitations: () => Promise<merchantUserMembershipInvitationInterface[]>
    registerMerchant: (data: registerMerchantFormInterface) => Promise<merchantInterface>
    fetchMerchants: () => Promise<merchantInterface[]>
    fetchMerchant: (merchantId: number | string) => Promise<merchantInterface>
    fetchMerchantOwner: (merchantId: number) => Promise<userInterface | undefined>
    fetchMerchantTags: (merchantId: number) => Promise<Tag[]>
    createMerchant: (data: createMerchantFormInterface) => Promise<merchantInterface>
    updateMerchant: (merchantId: number, data: updateMerchantFormInterface) => Promise<void>
    updateMerchantProfilePhoto: (merchantId: number, file: File) => Promise<void>
    updateMerchantName: (merchantId: number, name: string) => Promise<void>
    tagMerchant: (merchantId: number, tagId: number) => Promise<void>
    untagMerchant: (merchantId: number, tagId: number) => Promise<void>
}

export default (axios: AxiosInstance = baseAxios): MerchantsApiReturn => ({
    getFixCount: async (): Promise<FixCountResponse> => {
        const response = await axios.get<FixCountResponseRaw>('/api/merchants/fixCount');

        return toFixCount(response.data);
    },

    getFixStatus: async (): Promise<FixStatusResponse> => {
        const response = await axios.get<FixStatusResponse>('/api/merchants/fixStatus');

        return response.data;
    },

    loadClientBalance: async (clientId: number): Promise<clientBalanceResponseInterface> => {
        const response = await axios.get<ClientBalanceResponseRaw>(`/api/merchants/${clientId}/balances`);

        return toClientBalance(response.data);
    },

    fetchMerchantPendingInvitations: async (): Promise<merchantUserMembershipInvitationInterface[]> => {
        const response = await axios.get<{ data: merchantUserMembershipInvitationInterface[] }>('/api/merchants/invitations');

        return response.data.data;
    },

    registerMerchant: async (data: registerMerchantFormInterface): Promise<merchantInterface> => {
        const response = await axios.post<{ merchant: merchantInterface }>('/api/register-merchant', { name: data.name, primary_email: data.primaryEmail });

        return response.data.merchant;
    },

    fetchMerchants: async (): Promise<merchantInterface[]> => {
        const response = await axios.get<{ data: merchantInterface[] }>('/api/merchants');

        return response.data.data;
    },

    fetchMerchant: async (merchantId: number | string): Promise<merchantInterface> => {
        const response = await axios.get<{ data: merchantInterface }>(`/api/merchants/${merchantId}`);

        return response.data.data;
    },

    fetchMerchantOwner: async (merchantId: number): Promise<userInterface | undefined> => {
        const response = await axios.get<{ data?: userInterface }>(`/api/merchants/${merchantId}/owner`);

        return response.data.data;
    },

    fetchMerchantTags: async (merchantId: number): Promise<Tag[]> => {
        const response = await axios.get<{ data: TagResponse[] }>(`/api/merchants/${merchantId}/tags`);

        return response.data.data.map(toTag);
    },

    createMerchant: async (data: createMerchantFormInterface): Promise<merchantInterface> => {
        // invite_role carries no nullable rule on the backend, so an explicit null fails the
        // enum check when no invitation is wanted. Omit the pair rather than sending nulls.
        const invitation = data.inviteEmail !== null && data.inviteRole !== null
            ? { invite_email: data.inviteEmail, invite_role: data.inviteRole }
            : {};

        const response = await axios.post<{ merchant: merchantInterface }>('/api/merchants', {
            name: data.name,
            primary_email: data.primaryEmail,
            ...invitation,
        });

        return response.data.merchant;
    },

    updateMerchant: async (merchantId: number, data: updateMerchantFormInterface): Promise<void> => {
        await axios.put(`/api/merchants/${merchantId}`, fromUpdateMerchantForm(data));
    },

    updateMerchantProfilePhoto: async (merchantId: number, file: File): Promise<void> => {
        const data = new FormData();
        data.append('photo', file);

        await axios.post(`/api/merchants/${merchantId}/photo`, data);
    },

    updateMerchantName: async (merchantId: number, name: string): Promise<void> => {
        await axios.put(`/api/merchants/${merchantId}/name`, { name });
    },

    tagMerchant: async (merchantId: number, tagId: number): Promise<void> => {
        await axios.post(`/api/merchants/${merchantId}/tags/${tagId}`);
    },

    untagMerchant: async (merchantId: number, tagId: number): Promise<void> => {
        await axios.delete(`/api/merchants/${merchantId}/tags/${tagId}`);
    },
});
